import * as React from 'react';
import {
    DraggingStyle,
    NotDraggingStyle,
    DropResult,
    DragDropContext,
    Droppable,
    Draggable,
    DraggableLocation,
    DragStart,
} from 'react-beautiful-dnd';
import { HorizonInterval } from 'src/models/Horizon';
import { IDialogWizardAction } from 'src/components/common/DialogWizard';
import PortalAwareItem from '../PortalAwareItem';
import _ from 'lodash';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import { AnyObject } from 'react-final-form';

const portal: HTMLElement = document.createElement('div');
portal.classList.add('horizon-sequencing-portal');

if (!document.body) {
    throw new Error('body not ready for portal creation!');
}

document.body.appendChild(portal);

interface IHorizonSequencingComponentProps {
    horizons: Array<HorizonInterval>;
    intervalList?: HorizonInterval[][];
    unsequencedList?: HorizonInterval[];
    actions?: Array<IDialogWizardAction>;
    sequenceHorizons?: (sequence: Array<{ id: number; sequence: number }>, close: () => void) => void;
    onCancel?: () => void;
    onNext?: (intervals: Array<HorizonInterval>, close: () => void) => void;
    onPrev?: () => void;
    isInDialog?: boolean;
    hasMultipleTracts?: boolean;
    viewportRef?: React.MutableRefObject<HTMLDivElement>;
    customDetailFormat?: (interval: HorizonInterval, currIndex: number, sequenceLength: number) => JSX.Element;
    customListFormat?: (intervals: HorizonInterval[], currSequence: number, sequenceLength: number) => JSX.Element | JSX.Element[];
    reorderList?: (sourcePosition: number, destPosition: number, combine: boolean, sourceIndex?: number) => void;
}

interface IHorizonSequencingComponentState {
    horizons: Array<HorizonInterval>;
    dragging: boolean;
}

class HorizonSequencingComponent extends React.Component<IHorizonSequencingComponentProps, IHorizonSequencingComponentState> {
    private grid = 8;
    private viewportRef: React.MutableRefObject<HTMLDivElement> = React.createRef();

    constructor(props: IHorizonSequencingComponentProps) {
        super(props);

        this.state = {
            horizons: this.props.horizons,
            dragging: false,
        };
    }

    componentDidUpdate() {
        if (this.state.dragging) {
            window.addEventListener('mousemove', this.handleAutoScroll);
        } else {
            window.removeEventListener('mousemove', this.handleAutoScroll);
        }
    }

    getSourcePositions = (source: DraggableLocation, destPosition: number, isCombined: boolean) => {
        let sourcePosition: number = source.droppableId === 'unsequenced' ? -1 : source.index;
        let sourceIndex: number = source.droppableId === 'unsequenced' ? source.index : -1;
        this.props.reorderList(sourcePosition, destPosition, isCombined, sourceIndex);
    };

    reorderIntervals = (startIndex: number, endIndex: number) => {
        let result = Array.from(this.state.horizons);

        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        result = result.map((hi: HorizonInterval, index: number) => {
            hi.sequencePosition = index + 1;
            return hi;
        });

        return result;
    };

    getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle, item: HorizonInterval) => {
        if (!isDragging) {
            draggableStyle = { ...draggableStyle, transform: null };
        }

        return {
            margin: `${this.grid}px`,
            background: isDragging ? '#c8d9e9' : item?.id === 0 ? '#ffffc7' : '#f1f1f1',
            border: '2px solid #6393C1',
            display: 'flex',
            justifyContent: 'space-between',
            ...draggableStyle,
        };
    };

    getListStyle = (isDraggingOver: boolean) => ({
        background: isDraggingOver ? 'lightblue' : 'lightgrey',
        border: '2px dashed #6393C1',
        padding: this.grid,
        width: '100%'
    });

    onDragStart = (result: DragStart) => {
        this.setState({ dragging: true });
    }

    onDragEnd = (result: DropResult) => {
        this.setState({ dragging: false });
        if (!result.destination && !result.combine) {
            return;
        }

        if (this.props.hasMultipleTracts) {
            let combine = !!result.combine;
            let destPosition: number;
            if (combine) {
                destPosition = parseInt(result.combine.draggableId.substring(5), 10);
            } else {
                destPosition = result.destination.droppableId === 'unsequenced' ? -1 : result.destination.index;
            }

            this.getSourcePositions(result.source, destPosition, combine);
        } else {
            const items = this.reorderIntervals(result.source.index, result.destination.index);
            this.setState({ horizons: items });
        }
    };

    // TODO: Make this smoother. It doesn't work at a certain threshold for some reason, and the scrolling is clunky.
    handleAutoScroll = (event: MouseEvent) => {
        const viewport = this.viewportRef.current;

        if (!viewport) {
            return;
        }
    
        const { pageY } = event; // Mouse Y position, relative to the top of the page.
        const scrollThreshold = 50; // Can be adjusted to start the trigger.
        const scrollSpeed = 50; // Change as needed.
        const top = viewport.offsetTop; // Top and bottom of viewport are also relative to top of page.
        const bottom = viewport.offsetTop + viewport.offsetHeight;

        const scrollUp = pageY < top + scrollThreshold; // Scrolling up if too close to the top
        const scrollDown = pageY > bottom - scrollThreshold; // Scroll down
    
        if (scrollUp) {
          viewport.scrollBy(0, -scrollSpeed);
        } else if (scrollDown) {
            viewport.scrollBy(0, scrollSpeed);
        }

        // if (scrollUp && !this.state.scrolling) {
        //     this.setState({ scrolling: true });
        //     viewport.scrollBy(0, -scrollSpeed);
        // } else if (scrollDown && !this.state.scrolling) {
        //         this.setState({ scrolling: true });
        //         viewport.scrollBy(0, scrollSpeed);
        // } else {
        //         this.setState({ scrolling: false });
        // }  
    };

    handleSequenceHorizons = () => {
        if (this.props.unsequencedList?.length > 0) {
            window.alert('Please handle all unsequenced horizons.');
            return;
        }

        let indexedSequence: Array<{ id: number; sequence: number }>;
        let cancelProp = null;

        if (this.props.hasMultipleTracts) {
            indexedSequence = [];
            for (let i = 0; i < this.props.intervalList.length; i++) {
                if (this.props.intervalList[i].length > 0) {
                    let horizons = this.props.intervalList[i].map((h: HorizonInterval) => ({ id: h.id, sequence: i + 1 }));
                    indexedSequence = indexedSequence.concat(horizons);
                }
            }

        } else {
            indexedSequence = this.state.horizons.map((h: HorizonInterval, index: number) => ({ id: h.id, sequence: index + 1 }));
            cancelProp = this.props.onCancel;
        }

        this.props.sequenceHorizons(
            indexedSequence,
            cancelProp
        );
    };

    showButtons = () => {
        const style: React.CSSProperties = {
            cursor: 'pointer',
            background: '#6393C1',
            padding: '.5rem 1rem',
            color: 'white',
            display: 'inline-block',
            marginRight: '1rem',
        };

        if (this.props.isInDialog) {
            return (
                <div>
                    <span style={style} onClick={this.props.onPrev}>
                        Previous
                    </span>
                    <span style={style} onClick={() => this.props.onNext(this.state.horizons, this.props.onCancel)}>
                        Next
                    </span>
                    <span style={style} onClick={this.props.onCancel}>
                        Cancel
                    </span>
                </div>
            );
        } else {
            return (
                <div>
                    <span
                        style={style}
                        // onClick={() => this.props.sequenceHorizons({}}, this.props.onCancel)}
                        onClick={() => this.handleSequenceHorizons()}
                    >
                        Submit
                    </span>
                </div>
            );
        }
    };

    showHorizonSequence = (arr: HorizonInterval[], keyName: string) => {
        return arr.map((item: HorizonInterval, index: number) => {
            let uIndex = keyName === 'unsequenced' ? -1 : index;
            return (
                <Draggable key={`${keyName}-${item.id}`} draggableId={`${keyName}-${item.id}`} index={index}>
                    {(dragProvided, dragSnapshot) => (
                        <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                        >
                            <PortalAwareItem
                                item={item}
                                provided={dragProvided}
                                snapshot={dragSnapshot}
                                getItemStyle={(isDragging, draggableStyle) =>
                                    this.getItemStyle(isDragging, draggableStyle, item)
                                }
                                customDetailFormat={(draggable: AnyObject) => this.props.customDetailFormat(draggable as HorizonInterval, uIndex, this.props.intervalList?.length)}
                                portal={portal}
                            />
                        </div>
                    )}
                </Draggable>
            );
        });
    }

    showHorizonListSequence = (arr: HorizonInterval[][], keyName: string) => {
        return arr.map((item: HorizonInterval[], index: number) => {
            // Set to -1 if unsequenced
            let uIndex = keyName === 'unsequenced' ? -1 : index;

            return (
                <Draggable key={`${keyName}-${index}`} draggableId={`${keyName}-${index}`} index={index}>
                    {(dragProvided, dragSnapshot) => (
                        <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                        >
                            <PortalAwareItem
                                item={item[0]}
                                provided={dragProvided}
                                snapshot={dragSnapshot}
                                getItemStyle={(isDragging, draggableStyle) =>
                                    this.getItemStyle(isDragging, draggableStyle, item[0])
                                }
                                customDetailFormat={() => this.props.customListFormat(item, uIndex, this.props.intervalList?.length)}
                                portal={portal}
                            />
                        </div>
                    )}
                </Draggable>
            );
        });
    }

    public render() {
        const dndStyleWidth = this.props.unsequencedList?.length > 0 ? '50%' : '100%';

        return (
            <div>
                <div>
                    <div style={{ marginBottom: '.5rem', textAlign: 'center', fontWeight: 'bold' }}>Note: Drag and Drop Horizons Below to Resequence</div>
                    {(this.props.hasMultipleTracts && this.state.horizons) && (
                        <DragDropContext onDragStart={this.onDragStart} onDragEnd={this.onDragEnd}>
                            <div style={{ height: '50rem', display: 'flex', justifyContent: 'space-between' }}>
                                <div className="mr-1" style={{ width: '50%', overflow: 'auto' }}>
                                    <RHFLegend title="Unsequenced Horizons" />
                                    <Droppable droppableId={'unsequenced'}>
                                        {(provided, snapshot) => (
                                            <div {...provided.droppableProps} ref={provided.innerRef} style={this.getListStyle(snapshot.isDraggingOver)}>
                                                {this.showHorizonSequence(this.props.unsequencedList, 'unsequenced')}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                                {/* NOTE: The ViewportRef is set to always scroll this window. There shouldn't be a need to auto scroll the unsequenced one on drag. */}
                                <div ref={this.viewportRef} style={{ width: dndStyleWidth, overflow: 'auto' }}>
                                    <RHFLegend title="Sequenced Horizons" />
                                    <Droppable droppableId={'sequenced'} isCombineEnabled={true}>
                                        {(provided, snapshot) => (
                                            <div {...provided.droppableProps} ref={provided.innerRef} style={this.getListStyle(snapshot.isDraggingOver)}>
                                                {this.showHorizonListSequence(this.props.intervalList, 'item')}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </div>
                        </DragDropContext>
                    )}
                    {(!this.props.hasMultipleTracts && this.state.horizons) && (
                        <DragDropContext onDragStart={() => { this.setState({ dragging: true }); }} onDragEnd={this.onDragEnd}>
                            <Droppable droppableId="sequence">
                                {(provided, snapshot) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} style={this.getListStyle(snapshot.isDraggingOver)}>
                                        {this.showHorizonSequence(this.state.horizons, 'item')}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </div>
                <div className="dlc-form-action-bar">{this.showButtons()}</div>
            </div>
        );
    }
}

export default HorizonSequencingComponent;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/AddHorizonComponents/HorizonSequencingComponent.tsx